export interface SavedVisualization {
  id: string;
  name: string;
  description?: string;
  fileName: string;
  data: any[];
  headers: string[];
  createdAt: string;
  updatedAt: string;
  thumbnail?: string;
}

export interface VisualizationMetadata {
  totalSaved: number;
  lastSaved?: string;
  storageUsed: number;
}

const STORAGE_KEY = 'vizdata_saved_visualizations';
const MAX_STORAGE_SIZE = 50 * 1024 * 1024; // 50MB limit

export class StorageManager {
  private static instance: StorageManager;
  
  static getInstance(): StorageManager {
    if (!StorageManager.instance) {
      StorageManager.instance = new StorageManager();
    }
    return StorageManager.instance;
  }

  private constructor() {
    this.initializeStorage();
  }

  private initializeStorage(): void {
    if (!localStorage.getItem(STORAGE_KEY)) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([]));
    }
  }

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  private calculateStorageSize(): number {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? new Blob([data]).size : 0;
  }

  private compressData(data: any): string {
    try {
      return JSON.stringify(data);
    } catch (error) {
      console.error('Error compressing data:', error);
      throw new Error('Failed to compress data for storage');
    }
  }

  private decompressData(compressedData: string): any {
    try {
      return JSON.parse(compressedData);
    } catch (error) {
      console.error('Error decompressing data:', error);
      throw new Error('Failed to decompress stored data');
    }
  }

  saveVisualization(
    name: string,
    data: any[],
    headers: string[],
    fileName: string,
    description?: string
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      try {
        const visualizations = this.getAllVisualizations();
        const now = new Date().toISOString();
        
        const newVisualization: SavedVisualization = {
          id: this.generateId(),
          name: name.trim(),
          description: description?.trim(),
          fileName,
          data: data.slice(0, 3000), // Keep save operations responsive in browser storage
          headers,
          createdAt: now,
          updatedAt: now
        };

        // Check storage size before saving
        const testData = [...visualizations, newVisualization];
        const compressedTestData = this.compressData(testData);
        const testSize = new Blob([compressedTestData]).size;
        
        console.log('Storage check:', { testSize, maxSize: MAX_STORAGE_SIZE, currentCount: visualizations.length });
        
        if (testSize > MAX_STORAGE_SIZE) {
          reject(new Error('Storage limit exceeded. Please delete some saved visualizations.'));
          return;
        }

        visualizations.push(newVisualization);
        
        try {
          localStorage.setItem(STORAGE_KEY, this.compressData(visualizations));
          console.log('Visualization saved successfully:', newVisualization.id);
          resolve(newVisualization.id);
        } catch (storageError) {
          console.error('localStorage error:', storageError);
          reject(new Error(`Failed to save to localStorage: ${storageError}`));
        }
      } catch (error) {
        console.error('Error saving visualization:', error);
        reject(new Error('Failed to save visualization'));
      }
    });
  }

  getAllVisualizations(): SavedVisualization[] {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? this.decompressData(data) : [];
    } catch (error) {
      console.error('Error loading visualizations:', error);
      return [];
    }
  }

  getVisualization(id: string): SavedVisualization | null {
    const visualizations = this.getAllVisualizations();
    return visualizations.find(viz => viz.id === id) || null;
  }

  updateVisualization(
    id: string,
    updates: Partial<Omit<SavedVisualization, 'id' | 'createdAt'>>
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const visualizations = this.getAllVisualizations();
        const index = visualizations.findIndex(viz => viz.id === id);
        
        if (index === -1) {
          reject(new Error('Visualization not found'));
          return;
        }

        visualizations[index] = {
          ...visualizations[index],
          ...updates,
          updatedAt: new Date().toISOString()
        };

        localStorage.setItem(STORAGE_KEY, this.compressData(visualizations));
        resolve();
      } catch (error) {
        console.error('Error updating visualization:', error);
        reject(new Error('Failed to update visualization'));
      }
    });
  }

  deleteVisualization(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const visualizations = this.getAllVisualizations();
        const filteredVisualizations = visualizations.filter(viz => viz.id !== id);
        
        if (filteredVisualizations.length === visualizations.length) {
          reject(new Error('Visualization not found'));
          return;
        }

        localStorage.setItem(STORAGE_KEY, this.compressData(filteredVisualizations));
        resolve();
      } catch (error) {
        console.error('Error deleting visualization:', error);
        reject(new Error('Failed to delete visualization'));
      }
    });
  }

  getMetadata(): VisualizationMetadata {
    const visualizations = this.getAllVisualizations();
    const storageUsed = this.calculateStorageSize();
    
    return {
      totalSaved: visualizations.length,
      lastSaved: visualizations.length > 0 
        ? visualizations.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0].updatedAt
        : undefined,
      storageUsed
    };
  }

  exportVisualization(id: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      try {
        const visualization = this.getVisualization(id);
        if (!visualization) {
          reject(new Error('Visualization not found'));
          return;
        }

        const exportData = {
          ...visualization,
          exportedAt: new Date().toISOString(),
          version: '1.0'
        };

        const blob = new Blob([JSON.stringify(exportData, null, 2)], {
          type: 'application/json'
        });

        resolve(blob);
      } catch (error) {
        console.error('Error exporting visualization:', error);
        reject(new Error('Failed to export visualization'));
      }
    });
  }

  importVisualization(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const content = e.target?.result as string;
          const importedData = JSON.parse(content);
          
          // Validate imported data structure
          if (!importedData.data || !importedData.headers || !importedData.fileName) {
            reject(new Error('Invalid visualization file format'));
            return;
          }

          const id = await this.saveVisualization(
            importedData.name || 'Imported Visualization',
            importedData.data,
            importedData.headers,
            importedData.fileName,
            importedData.description
          );

          resolve(id);
        } catch (error) {
          console.error('Error importing visualization:', error);
          reject(new Error('Failed to import visualization'));
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsText(file);
    });
  }

  clearAllData(): Promise<void> {
    return new Promise((resolve) => {
      localStorage.removeItem(STORAGE_KEY);
      this.initializeStorage();
      resolve();
    });
  }

  getStorageQuota(): { used: number; total: number; percentage: number } {
    const used = this.calculateStorageSize();
    const total = MAX_STORAGE_SIZE;
    const percentage = Math.round((used / total) * 100);
    
    return { used, total, percentage };
  }
}
